import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  DEPARTMENT_LABEL,
  LAB_NOTE_MAX,
  conversationRows,
  mayPostLabNote,
  privateReplyTo,
  quietDelivered,
  seesEveryNote,
  userColorIndex,
  type CasePhoto,
  type ConversationMessage,
  type Kit,
  type KitNote,
  type NoteKind,
  type OutgoingNote,
} from '@/domain';
import { useClock } from '@/application/container';
import {
  useKitWorkProofs,
  useMarkNotesRead,
  useNoteThread,
  useSendLabNote,
} from '@/application/usecases/useKits';
import { useSession } from '@/application/usecases/useSession';
import { EntryProofs } from '@/features/queue/WorkProofStrip';
import { useIsOnline } from '@/application/outbox/useOutboxSync';
import { formatRecent } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { Freshness } from '@/ui/feedback/Freshness';
import { SkeletonRows } from '@/ui/feedback/Loading';
import {
  AlertIcon,
  CameraIcon,
  CheckIcon,
  ChevronIcon,
  ClockIcon,
  FlagIcon,
  GoIcon,
  LockIcon,
  NoteIcon,
  PersonIcon,
  ReworkIcon,
  SendIcon,
  ShipIcon,
} from '@/ui/icons';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { enter, reducedMotion, riseFrom } from '@/ui/motion/motion';
import { useListMotion } from '@/ui/motion/useListMotion';
import { FullScreenPage } from '@/ui/primitives/FullScreenPage';
import styles from './LabNotesSheet.module.css';

/**
 * The space above a row that starts a group, as the stylesheet has it. A sent
 * message travels its own height and this, and no further.
 */
const GROUP_GAP_PX = 12;

const EVENT_ICON: Partial<Record<NoteKind, typeof NoteIcon>> = {
  flag: FlagIcon,
  rework: ReworkIcon,
  done: CheckIcon,
  passed: CheckIcon,
  resolved: CheckIcon,
  assign: PersonIcon,
  move: GoIcon,
  proof: CameraIcon,
  // The two steps of a case after it leaves the lab: the van it left in, and
  // the tick of a thing finished. The request of 5 October 2026 (third), section 11.
  shipped: ShipIcon,
  received: CheckIcon,
};

const KIND_LABEL: Record<NoteKind, string> = {
  note: strings.notes.kindNote,
  rework: strings.notes.kindRework,
  flag: strings.notes.kindFlag,
  hold: strings.notes.kindHold,
  deletion: strings.notes.kindDeletion,
  redo: strings.notes.kindRedo,
  done: strings.notes.kindDone,
  assign: strings.notes.kindAssign,
  move: strings.notes.kindMove,
  resolved: strings.notes.kindResolved,
  passed: strings.notes.kindPassed,
  proof: strings.notes.kindProof,
  shipped: strings.notes.kindShipped,
  received: strings.notes.kindReceived,
};

/** Today's messages by the clock alone, this week's by the day's name, older ones with their date. */
function when(iso: string): string {
  return formatRecent(iso);
}

/**
 * A case's Lab Notes, as a conversation. Request of 4 October 2026, section 7.
 *
 * Oldest at the top and newest at the bottom, opened scrolled to the newest.
 * What the system recorded (flagged with its reason, marked done with its note,
 * assigned, moved, resolved) is drawn as a line across the conversation; what a
 * person wrote is a message with their name, department and time. A private
 * message carries a lock and says who alone can read it.
 *
 * The server decides what is in the list: a private note is never sent to
 * anybody it is not for. The screen only offers what the server would accept,
 * a private reply to somebody who wrote privately, or, for management and
 * supervisors, a private note to the person the case is assigned to.
 *
 * Since the request of 5 October 2026 (third), section 4: one message is one
 * row, with one key, from the moment Send is pressed (`conversationRows` in the
 * domain says which rows there are). A row sent here is on screen at once,
 * says "Sending" and then "Sent" under itself, and is never taken out and put
 * back, which is what used to flicker, jump and show twice. Messages by one
 * person within a minute share one name and time; the reader's own are on the
 * right with no name; everybody else's are on the left under their name, in
 * that person's colour.
 */
export function LabNotesSheet({
  kit,
  onClose,
  page = false,
}: {
  kit: Kit;
  onClose: () => void;
  /**
   * A page over the whole screen, for a phone, in place of the sheet. Request
   * of 4 October 2026 (second), section 4.
   */
  page?: boolean;
}) {
  const { data: session } = useSession();
  const thread = useNoteThread(kit.id, true);
  // The photographs behind a proof row: the caller's own, or everybody's for a
  // holder of seeWorkProof. The request of 5 October 2026 (third), section 6.
  const proofs = useKitWorkProofs(kit.id).data;
  const send = useSendLabNote();
  const markRead = useMarkNotesRead();
  const [text, setText] = useState('');
  const clock = useClock();
  const [outgoing, setOutgoing] = useState<OutgoingNote[]>([]);
  const field = useRef<HTMLTextAreaElement>(null);

  const me = session?.user.id ?? '';
  // An accountant kept to Payments, Items and Archive reads a case and does
  // not write on it: the server refuses the post, so the composer is not
  // offered. The request of 5 October 2026 (third), section 10. One who holds
  // any other screen writes like anybody else, since the request of
  // 6 October 2026 (fourth): the role and the screens of the session decide.
  const mayPost = mayPostLabNote(session?.role, session?.views ?? []);
  const seesAll =
    session !== undefined &&
    session !== null &&
    seesEveryNote({ permissions: session.permissions, isSupervisor: session.user.isSupervisor });

  // As the server sends them, newest first. The domain turns them, and what
  // was sent from here, into the rows of the conversation, oldest first.
  const served = thread.data?.items;
  const rows = conversationRows(served ?? [], outgoing, me);
  const replyTo = privateReplyTo(served ?? [], me);

  // Who a private message would go to: whoever wrote privately to me, or, for
  // somebody who reads every note, the person the case is assigned to.
  const assignee =
    kit.assignedToUserId !== null && kit.assignedToUserId !== me && kit.assignedToUserName !== null
      ? { id: kit.assignedToUserId, name: kit.assignedToUserName }
      : null;
  const privateTarget = replyTo ?? (seesAll ? assignee : null);
  // A reply to a private note is private unless the writer chooses otherwise.
  const [privacy, setPrivacy] = useState<'everyone' | 'private' | null>(null);
  const goesPrivate =
    privateTarget !== null &&
    (privacy ?? (replyTo !== null ? 'private' : 'everyone')) === 'private';

  // Read once on open and again whenever a new message arrives while it is open.
  const newest = served?.[0]?.id ?? '';
  const { mutate: markAsRead } = markRead;
  const loaded = thread.data !== undefined;
  useEffect(() => {
    if (loaded) markAsRead(kit.id);
  }, [newest, loaded, kit.id, markAsRead]);

  /*
   * Opened at the newest, and kept there as messages arrive: smoothly, unless
   * the reader has scrolled up to read something older. Then the conversation
   * stays where they are and a "New messages" pill offers the way down.
   * Request of 5 October 2026, section 7.
   *
   * Since the request of 5 October 2026 (third), section 4, it follows the
   * rows by their keys, so it can tell what it is looking at. A message the
   * reader just sent is a new key: the conversation goes to the end at once and
   * rises from where it was, the new bubble coming out from behind the composer
   * and the older ones going up with it. Its confirmation is no new key, so
   * nothing moves a second time. Somebody else's message is followed or, while
   * the reader is further up, counted on the pill; the reader's own never are.
   *
   * The scroller's own `scrollTop` is written, never `scrollIntoView`, which
   * scrolls every ancestor and can pan an iPhone's visual viewport with the
   * keyboard open, and the page's frame follows that.
   */
  const [unseen, setUnseen] = useState(0);
  const opened = useRef(false);
  const seen = useRef<ReadonlySet<string>>(new Set());
  // New notes rise in where they belong; the first open shows the newest on
  // screen in sequence. A row sent from here plays its own arrival, below.
  const list = useListMotion<HTMLOListElement>(rows.map((row) => row.key));
  const scrollerOf = () => {
    let node = list.current?.parentElement ?? null;
    while (node !== null) {
      const { overflowY } = getComputedStyle(node);
      if (overflowY === 'auto' || overflowY === 'scroll') return node;
      node = node.parentElement;
    }
    return null;
  };
  const rowElement = (key: string) =>
    [...(list.current?.children ?? [])].find(
      (child): child is HTMLElement => child instanceof HTMLElement && child.dataset.motionKey === key,
    ) ?? null;
  const toNewest = (smooth: boolean) => {
    const scroller = scrollerOf();
    if (scroller !== null) {
      if (smooth && !reducedMotion() && typeof scroller.scrollTo === 'function') {
        scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
      } else {
        scroller.scrollTop = scroller.scrollHeight;
      }
    }
    setUnseen(0);
  };
  const rowKeys = rows.map((row) => row.key).join('\u0000');
  useLayoutEffect(() => {
    const before = seen.current;
    const fresh = rows.filter((row) => !before.has(row.key));
    seen.current = new Set(rows.map((row) => row.key));
    if (!opened.current) {
      if (loaded || rows.length > 0) opened.current = true;
      toNewest(false);
      return;
    }
    if (fresh.length === 0) return;

    const scroller = scrollerOf();
    const sent = fresh.filter((row) => row.type === 'message' && row.sentHere).at(-1);
    if (sent !== undefined) {
      const row = rowElement(sent.key);
      let moved = 0;
      if (scroller !== null) {
        const from = scroller.scrollTop;
        scroller.scrollTop = scroller.scrollHeight;
        moved = scroller.scrollTop - from;
      }
      if (reducedMotion()) enter(row, 'fade');
      else if (moved >= 1 && row !== null) {
        riseFrom(list.current, Math.min(moved, row.offsetHeight + GROUP_GAP_PX));
      } else enter(row, 'rise');
      setUnseen(0);
      return;
    }

    const reading =
      scroller !== null && scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight > 120;
    if (!reading) {
      toNewest(true);
      return;
    }
    // The pill counts what somebody else wrote and what the system recorded.
    const news = fresh.filter((row) => row.type === 'event' || !row.mine).length;
    if (news > 0) setUnseen((count) => count + news);
    // Only a new row moves the conversation; a row that changes does not. The
    // keys stand for the rows, and the helpers read the list as it is now.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowKeys, loaded]);
  // Reaching the bottom by hand clears the pill.
  useEffect(() => {
    const scroller = scrollerOf();
    if (scroller === null || unseen === 0) return;
    const onScroll = () => {
      if (scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 40) setUnseen(0);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
    // The scroller is found from the list each time; it is not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unseen]);
  /*
   * Each message waits for its own answer. The request's promise is the
   * message's own; the callbacks `mutate` takes belong to the shared observer
   * and are overwritten by the next call, so a second message sent before the
   * first had answered left the first saying "Sending" for good, beside its
   * saved copy. A message is never taken out of the list on success: it is what
   * ties the server's note to the row already on screen.
   */
  const settle = (clientId: string, change: Partial<OutgoingNote>) =>
    setOutgoing((current) =>
      current.map((entry) => (entry.clientId === clientId ? { ...entry, ...change } : entry)),
    );

  function deliver(message: OutgoingNote) {
    settle(message.clientId, { state: 'sending' });
    send
      .mutateAsync({
        kitId: kit.id,
        text: message.text,
        requestId: message.clientId,
        visibleToUserId: message.visibleTo?.id ?? null,
      })
      .then(
        (note) => settle(message.clientId, { state: 'sent', noteId: note.id }),
        () => settle(message.clientId, { state: 'failed' }),
      );
  }

  function retry(clientId: string) {
    const message = outgoing.find((entry) => entry.clientId === clientId);
    if (message !== undefined) deliver(message);
  }

  function discard(clientId: string) {
    setOutgoing((current) => current.filter((entry) => entry.clientId !== clientId));
  }

  function submit() {
    // As the server keeps it, so its copy can be told by its words: trimmed, then cut.
    const trimmed = text.trim().slice(0, LAB_NOTE_MAX);
    if (trimmed === '') return;
    const message: OutgoingNote = {
      clientId: crypto.randomUUID(),
      text: trimmed,
      visibleTo: goesPrivate ? privateTarget : null,
      sentAt: clock.now().toISOString(),
      state: 'sending',
      noteId: null,
      knownIds: (served ?? []).map((note) => note.id),
      showsStatus: true,
    };
    // "Sent" stays under the newest message until the next one is sent: now.
    const arrived = rows
      .filter((row) => row.type === 'message' && row.delivery === 'sent')
      .map((row) => row.key);
    setOutgoing((current) => [...quietDelivered(current, arrived), message]);
    setText('');
    deliver(message);
    // The keyboard stays up for the next message.
    field.current?.focus();
  }

  // What is on screen is the saved copy: offline, or the last fetch failed.
  const online = useIsOnline();
  const stale = thread.data !== undefined && (!online || thread.isRefetchError);

  const frame = {
    title: `${strings.labNotes.title} · ${kit.jobOrderNo}`,
    onClose,
  };

  const action = (
    <Freshness
      updatedAt={thread.dataUpdatedAt}
      fetching={thread.isFetching}
      stale={stale}
      onRefresh={() => void thread.refetch()}
    />
  );

  const composer = (
    <div className={styles.composer}>
      {privateTarget !== null && (
        <div className={styles.privacy} role="radiogroup" aria-label={strings.labNotes.whoSees}>
          <button
            type="button"
            role="radio"
            aria-checked={!goesPrivate}
            className={!goesPrivate ? styles.on : ''}
            onClick={() => setPrivacy('everyone')}
          >
            {strings.labNotes.everyone}
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={goesPrivate}
            className={goesPrivate ? styles.on : ''}
            onClick={() => setPrivacy('private')}
          >
            <LockIcon size={13} />
            {strings.labNotes.privateTo(privateTarget.name)}
          </button>
        </div>
      )}
      <div className={styles.write}>
        <textarea
          ref={field}
          aria-label={strings.labNotes.write}
          placeholder={strings.labNotes.placeholder}
          rows={1}
          value={text}
          maxLength={LAB_NOTE_MAX}
          data-autofocus
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) submit();
          }}
        />
        <button
          type="button"
          className={styles.send}
          aria-label={strings.labNotes.send}
          disabled={text.trim() === ''}
          // Pressing a button takes the focus, and on a phone that closes the
          // keyboard and resizes the page under the message as it arrives.
          // Moving the focus is what mousedown does by default, on touch too.
          onMouseDown={(event) => event.preventDefault()}
          onClick={submit}
        >
          <SendIcon size={18} />
        </button>
      </div>
    </div>
  );

  const conversation = (
    <>
      {thread.isPending && <SkeletonRows rows={4} label={strings.labNotes.loading} />}
      {thread.isError && thread.data === undefined && (
        <p className={styles.empty}>{strings.errors.server}</p>
      )}
      {thread.data !== undefined && rows.length === 0 && (
        <p className={styles.empty}>{strings.labNotes.empty}</p>
      )}

      <ol ref={list} className={styles.list} aria-label={strings.labNotes.title}>
        {rows.map((row) =>
          row.type === 'event' ? (
            <EventRow
              key={row.key}
              note={row.note}
              proofs={proofs}
              jobOrderNo={kit.jobOrderNo}
            />
          ) : (
            <MessageRow
              key={row.key}
              row={row}
              me={me}
              onRetry={() => retry(row.key)}
              onDiscard={() => discard(row.key)}
            />
          ),
        )}
      </ol>
      {unseen > 0 && (
        <button type="button" className={styles.newPill} onClick={() => toNewest(true)}>
          {strings.labNotes.newMessages}
          <ChevronIcon size={14} />
        </button>
      )}
    </>
  );

  return page ? (
    <FullScreenPage
      title={kit.jobOrderNo}
      kicker={strings.labNotes.title}
      onClose={onClose}
      closeLabel={strings.labNotes.back}
      action={action}
      footer={mayPost ? composer : undefined}
      pinToEnd
    >
      {conversation}
    </FullScreenPage>
  ) : (
    <BottomSheet
      title={frame.title}
      onClose={frame.onClose}
      lead={strings.labNotes.lead(kit.jobOrderNo)}
      closeLabel={strings.labNotes.close}
      tall
      action={action}
      footer={mayPost ? composer : undefined}
    >
      {conversation}
    </BottomSheet>
  );
}

function EventRow({
  note,
  proofs,
  jobOrderNo,
}: {
  note: KitNote;
  /** The proofs the caller was given for the case; undefined while they load. */
  proofs: readonly CasePhoto[] | undefined;
  jobOrderNo: string;
}) {
  const Icon = EVENT_ICON[note.kind] ?? NoteIcon;
  const undone = note.undoneAt != null;
  return (
    <li
      data-motion-key={note.id}
      className={`${styles.event} ${styles[note.kind] ?? ''} ${undone ? styles.undone : ''}`}
    >
      <div className={styles.eventLine}>
        <Icon size={14} />
        <span className={styles.eventText}>{note.event ?? KIND_LABEL[note.kind]}</span>
      </div>
      {/*
        Taken back: the entry is never removed, so the history is honest. It
        says so in words, not by the line through it alone. Section 6 of the
        request of 5 October 2026.
      */}
      {undone && (
        <div className={styles.undoneMark} data-testid="note-undone">
          {note.undoneByName != null && note.undoneByName !== ''
            ? strings.labNotes.undoneBy(note.undoneByName)
            : strings.labNotes.undone}
          {' · '}
          {when(note.undoneAt as string)}
        </div>
      )}
      {note.text !== '' && <div className={styles.eventNote}>{note.text}</div>}
      {/*
        A proof row shows the photographs of its stage visit, and a tile opens
        the image viewer on them. Nothing for a reader who was given none. The
        request of 5 October 2026 (third), section 6.
      */}
      {note.kind === 'proof' && (
        <EntryProofs
          className={styles.eventPhotos}
          proofs={proofs}
          historyEntryId={note.historyEntryId}
          jobOrderNo={jobOrderNo}
        />
      )}
      {note.visibleTo !== null && (
        <div className={styles.lock}>
          <LockIcon size={12} />
          {strings.labNotes.onlyVisibleTo(note.visibleTo.name)}
        </div>
      )}
      <div className={styles.eventMeta}>
        {note.byUserName === '' ? strings.notes.bySystem : note.byUserName} · {when(note.createdAt)}
      </div>
    </li>
  );
}

/**
 * A message somebody wrote, whether it is on its way or long since saved: one
 * component and one element for both, so a message that arrives changes what
 * its status line says and nothing else. The request of 5 October 2026 (third),
 * section 4.
 *
 * Always in the same order: who and when (on the first of a group), the bubble,
 * the status line (on one sent from this screen). Somebody else's carries their
 * name in their colour over a bubble tinted with it; the name is always there,
 * for a reader of the screen on the rows of a group that do not show it too.
 * The reader's own are on the right with no name, and say "You" only to a
 * screen reader.
 */
function MessageRow({
  row,
  me,
  onRetry,
  onDiscard,
}: {
  row: ConversationMessage;
  me: string;
  onRetry: () => void;
  onDiscard: () => void;
}) {
  const author = row.mine ? strings.labNotes.you : row.byUserName;
  const waiting = row.delivery === 'sending' || row.delivery === 'failed';
  return (
    <li
      data-motion-key={row.key}
      data-motion-arrival={row.sentHere ? 'none' : undefined}
      data-user-color={!row.mine && row.byUserId !== null ? userColorIndex(row.byUserId) : undefined}
      className={[
        styles.message,
        row.mine ? styles.mine : '',
        row.first ? styles.first : styles.next,
        row.last ? styles.last : '',
        waiting ? styles.pending : '',
      ]
        .filter((name) => name !== '')
        .join(' ')}
    >
      {row.first ? (
        <div className={styles.meta}>
          {row.mine ? <span className="visually-hidden">{author}</span> : <b>{author}</b>}
          {!row.mine && row.byUserDepartment !== null && (
            <span>{DEPARTMENT_LABEL[row.byUserDepartment]}</span>
          )}
          <time dateTime={row.at}>{when(row.at)}</time>
        </div>
      ) : (
        <span className="visually-hidden">
          {author} · <time dateTime={row.at}>{when(row.at)}</time>
        </span>
      )}
      <div className={`${styles.bubble} ${row.visibleTo !== null ? styles.private : ''}`}>
        {row.visibleTo !== null && (
          <div className={styles.lock}>
            <LockIcon size={12} />
            {strings.labNotes.onlyVisibleTo(
              row.visibleTo.id === me ? strings.labNotes.you.toLowerCase() : row.visibleTo.name,
            )}
          </div>
        )}
        <div className={styles.text}>{row.text}</div>
      </div>
      {/*
        There from the row's first paint, in the same place and at the same
        height whatever it says, so nothing under it moves when it changes.
      */}
      {row.delivery !== null && (
        <div
          className={`${styles.status} ${row.delivery === 'failed' ? styles.failed : ''}`}
          role={row.delivery === 'failed' ? 'alert' : 'status'}
        >
          {row.delivery === 'failed' ? (
            <>
              <AlertIcon size={12} />
              <span>{strings.labNotes.failed}</span>
              <button type="button" onClick={onRetry}>
                {strings.labNotes.retry}
              </button>
              <button type="button" onClick={onDiscard}>
                {strings.labNotes.discard}
              </button>
            </>
          ) : (
            <>
              {row.delivery === 'sending' ? <ClockIcon size={12} /> : <CheckIcon size={12} />}
              <span>
                {row.delivery === 'sending' ? strings.labNotes.sending : strings.labNotes.sent}
              </span>
            </>
          )}
        </div>
      )}
    </li>
  );
}
